import './TeamEmailsView.scss';

import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { partial, throttle } from 'lodash';
import PropTypes from 'prop-types';

import { showConfirmModal } from 'actions/modals';
import {
  actionTypes as teamEmailsActionTypes,
  clearTeamEmails,
  fetchTeamEmails,
  setCurrentEmailId,
  setShowNewEmailView,
  unpinTeamAnnouncement,
} from 'actions/teamEmails';

import { getLocationsIndex } from 'selectors/entities';
import { getIsFetching } from 'selectors/fetching';
import {
  getCurrentUserIsManagerial,
  getHasFeature,
  getShowAnnouncementsV2,
  getTeamEmailAudiences,
} from 'selectors/session';
import {
  getCurrentEmail,
  getShowNewEmailView,
  getTeamAnnouncements,
} from 'selectors/teamEmails';

import {
  MessageDrawerListEmptyState,
  MessageDrawerListFooter,
  MessageDrawerListItem,
  MessageDrawerListView,
} from 'features/messengerv2/MessageDrawerList';

import cxHelpers from 'util/className';
import { moment } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import EmailItem from './EmailItem';
import NewEmail from './NewEmail';
import Paywall from './Paywall';
import ReadListView from './ReadListView';

const THROTTLE_WAIT = 16;

@connect(
  (state, props) => ({
    isFetching:
      getIsFetching(state, {
        requestId: teamEmailsActionTypes.FETCH_TEAM_EMAILS_REQUEST,
      }) ||
      getIsFetching(state, {
        requestId: teamEmailsActionTypes.FETCH_TEAM_ANNOUNCEMENTS_REQUEST,
      }),
    showNewEmailView: getShowNewEmailView(state, props),
    currentEmail: getCurrentEmail(state, props),
    teamAnnouncements: getTeamAnnouncements(state, props),
    teamEmailAudiences: getTeamEmailAudiences(state, props),
    locations: getLocationsIndex(state, props),
    showAnnouncementsV2: getShowAnnouncementsV2(state, props),
    isManagerial: getCurrentUserIsManagerial(state, props),
    announcementsEnabled: getHasFeature(state, {
      feature: 'announcements',
    }),
  }),
  {
    fetchTeamEmails,
    clearTeamEmails,
    setCurrentEmailId,
    setShowNewEmailView,
    unpinTeamAnnouncement,
    showConfirmModal,
  }
)
@cxHelpers('TeamEmailsView')
export default class TeamEmailsView extends Component {
  static propTypes = {
    currentEmail: ImmutablePropTypes.map,
    isFetching: PropTypes.bool,
    showNewEmailView: PropTypes.bool,
    fetchTeamEmails: PropTypes.func.isRequired,
    teamAnnouncements: ImmutablePropTypes.list,
    clearTeamEmails: PropTypes.func.isRequired,
    setShowNewEmailView: PropTypes.func.isRequired,
    setCurrentEmailId: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    locations: ImmutablePropTypes.map.isRequired,
    teamEmailAudiences: ImmutablePropTypes.list.isRequired,
    onReturn: PropTypes.func,
    showAnnouncementsV2: PropTypes.bool,
    unpinTeamAnnouncement: PropTypes.func,
    showConfirmModal: PropTypes.func,
    isManagerial: PropTypes.bool.isRequired,
    announcementsEnabled: PropTypes.bool.isRequired,
  };

  state = {
    summaryAnnouncement: null,
  };

  componentDidMount() {
    if (!this.props.showAnnouncementsV2) {
      this.fetchPaginatedEmails();
    }
  }

  shouldComponentUpdate(nextProps) {
    return !(
      !this.props.isFetching &&
      nextProps.isFetching &&
      this.props.teamAnnouncements.size > 0
    );
  }

  componentWillUnmount() {
    if (this.container) this.unbindScrollListener();
    if (!this.props.showAnnouncementsV2) this.props.clearTeamEmails();
  }

  handleClickNewEmail = () => {
    this.props.setShowNewEmailView(true);
  };

  handleReturn = () => {
    this.props.setCurrentEmailId(null);
    this.props.setShowNewEmailView(false);

    if (this.props.onReturn) {
      this.props.onReturn();
    }
  };

  handleClose = () => {
    this.props.setCurrentEmailId(null);
    this.props.onClose();
  };

  handleUnpin = emailProps => {
    this.props.showConfirmModal({
      altType: true,
      auto: true,
      width: 440,
      className: this.cxEl('unpin_modal'),
      title: toI18n('message_drawer.team_emails.confirm_modal.title'),
      message: toI18n('message_drawer.team_emails.confirm_modal.desc'),
      onConfirm: () => {
        this.props.unpinTeamAnnouncement(emailProps.toJS());
      },
      showCustomIcon: false,
      submitText: toI18n(
        'message_drawer.team_emails.confirm_modal.submit_text'
      ),
      cancelText: 'Cancel',
    });
  };

  handleCloseSummary = () => this.setState({ summaryAnnouncement: null });

  showAnnouncementSummary = (summaryAnnouncement, e) => {
    e.stopPropagation();
    e.preventDefault();

    this.setState({ summaryAnnouncement });
  };

  handleScroll = () => {
    if (!this.props.showAnnouncementsV2 && this.shouldFetchMore()) {
      this.fetchPaginatedEmails();
    }
  };

  bindScrollListener(container) {
    findDOMNode(container).addEventListener(
      'scroll',
      this.throttledHandleScroll
    );
  }

  unbindScrollListener() {
    findDOMNode(this.container).removeEventListener(
      'scroll',
      this.throttledHandleScroll
    );
  }

  throttledHandleScroll = throttle(this.handleScroll, THROTTLE_WAIT);

  shouldFetchMore() {
    const { isFetching, showNewEmailView, currentEmail } = this.props;
    const { scrollTop, scrollHeight, offsetHeight } = this.container;

    return (
      !(
        showNewEmailView ||
        isFetching ||
        currentEmail ||
        this.lastFetchedEmailId === this.getLastEmailId()
      ) && scrollTop + offsetHeight >= scrollHeight
    );
  }

  getLastEmailId() {
    const lastEmail = this.props.teamAnnouncements.last();

    return lastEmail && lastEmail.get('id');
  }

  fetchPaginatedEmails() {
    this.lastFetchedEmailId = this.getLastEmailId();
    this.props.fetchTeamEmails(this.lastFetchedEmailId);
  }

  getContainerRef = container => {
    if (this.container) this.unbindScrollListener();
    if (container) this.bindScrollListener(container);
    this.container = container;
  };

  renderEmptyState() {
    if (this.props.showAnnouncementsV2) {
      return (
        <div>
          <MessageDrawerListEmptyState
            title="message_drawer.team_announcements.empty_state"
            icon="envelopeBlue"
          />
          {this.props.isManagerial && this.renderFooter()}
        </div>
      );
    }

    return (
      <div>
        <MessageDrawerListEmptyState
          title="message_drawer.team_emails.empty_view.title"
          description="message_drawer.team_emails.empty_view.desc"
          icon="envelopeBlue"
        />
        {this.renderFooter()}
      </div>
    );
  }

  renderAnnouncement = announcement => {
    const {
      id,
      subject,
      body,
      audience,
      created_at: createdAt,
      location_name: locationName,
      pinned,
      requires_acknowledgment: requiresAcknowledgement,
      needs_acknowledgment: needsAcknowledgment,
      attachments = [],
      acknowledged_count: acknowledgedCount,
      recipients_count: recipientsCount,
    } = announcement.toJS();

    const sentAt = moment(createdAt, 'MM/DD/YYYY HH:mm').format('ddd D MMM');
    const sentTo = [
      toI18n(`message_drawer.team_emails.audiences.${audience}`),
      toI18n('at'),
      locationName,
    ].join(' ');

    return (
      <MessageDrawerListItem
        key={id}
        title={subject}
        subTitle={body}
        time={sentAt}
        footer={sentTo}
        attachmentsCount={attachments.length}
        needsAcknowledgment={needsAcknowledgment}
        requiresAcknowledgment={requiresAcknowledgement}
        handleClick={partial(this.props.setCurrentEmailId, id)}
        pinned={pinned}
        showPinnedPill={!this.props.showAnnouncementsV2}
        acknowledgedCount={acknowledgedCount}
        recipientsCount={recipientsCount}
        showAnnouncementSummary={partial(
          this.showAnnouncementSummary,
          announcement
        )}
        isManagerial={this.props.isManagerial}
      />
    );
  };

  renderRow = email => {
    const {
      id,
      subject,
      body,
      audience,
      created_at: createdAt,
      location_name: locationName,
      pinned,
    } = email.toJS();

    const sentAt = moment(createdAt, 'MM/DD/YYYY HH:mm').format('ddd D MMM');
    const sentTo = [
      toI18n(`message_drawer.team_emails.audiences.${audience}`),
      toI18n('at'),
      locationName,
    ].join(' ');

    return (
      <MessageDrawerListItem
        key={id}
        title={subject}
        subTitle={body}
        time={sentAt}
        footer={sentTo}
        handleClick={partial(this.props.setCurrentEmailId, id)}
        pinned={pinned}
        showPinnedPill={!this.props.showAnnouncementsV2}
      />
    );
  };

  renderFooter() {
    return (
      <MessageDrawerListFooter
        positionBottom={24}
        title="message_drawer.team_emails.new_announcement"
        handleClick={this.handleClickNewEmail}
        btnClassName={this.cxEl('new-email')}
      />
    );
  }

  renderAnnouncements() {
    const { isFetching, teamAnnouncements } = this.props;

    return (
      <MessageDrawerListView
        isLoading={isFetching}
        ref={this.getContainerRef}
        dataSource={teamAnnouncements}
        className={this.cxEl('list')}
        footer={this.props.isManagerial ? this.renderFooter() : null}
        emptyState={this.renderEmptyState()}
        renderRow={
          this.props.showAnnouncementsV2
            ? this.renderAnnouncement
            : this.renderRow
        }
        trackEventAction="Announcements Shown"
      />
    );
  }

  renderBody() {
    const {
      currentEmail,
      locations,
      teamEmailAudiences,
      showAnnouncementsV2,
      showNewEmailView,
    } = this.props;

    if (showAnnouncementsV2 && !this.props.announcementsEnabled) {
      return <Paywall />;
    }

    if (currentEmail) {
      return (
        <EmailItem
          announcement={currentEmail}
          showAnnouncementsV2={showAnnouncementsV2}
          onReturn={this.handleReturn}
          onClose={this.handleClose}
          onUnpin={this.handleUnpin}
          showSummary={this.showAnnouncementSummary}
          isCurrentUserManager={this.props.isManagerial}
        />
      );
    }

    if (showNewEmailView) {
      return (
        <NewEmail
          locations={locations}
          teamEmailAudiences={teamEmailAudiences}
          showAnnouncementsV2={showAnnouncementsV2}
          onReturn={this.handleReturn}
          onClose={this.handleClose}
        />
      );
    }

    return this.renderAnnouncements();
  }

  render() {
    return (
      <>
        {this.renderBody()}

        {this.state.summaryAnnouncement && (
          <ReadListView
            teamAnnouncement={this.state.summaryAnnouncement}
            onClose={this.handleCloseSummary}
          />
        )}
      </>
    );
  }
}
